import { css } from "@emotion/css";
import { fontSize, fontWeight, space } from "@octopusdeploy/design-system-tokens";
import type { LicenseLimitUsage, LicenseStatusResource, LicenseUsageResource, Repository } from "@octopusdeploy/octopus-server-client";
import { LimitStatus } from "@octopusdeploy/octopus-server-client";
import moment from "moment";
import * as React from "react";
import { getKnownLicenseUsageNames, isKnownLicenseUsageName } from "~/areas/configuration/components/LicenseUsage/KnownLicenseUsageName";
import { PageContent } from "~/components/PageContent/PageContent";
import Section from "~/components/Section";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import { LicenseCallout } from "./LicenseCallout";
import { LicenseUsageCallout } from "./LicenseUsageCallout";
import { LicenseUsageCard } from "./LicenseUsageCard";
import { LimitUsageCard } from "./LimitUsageCard";
import { UsageDetailTable } from "./UsageDetailTable";
import { getLicenseUsageContextualHelp } from "./getLicenseUsageContextualHelp";
interface LicenseUsageDashboardPageProps {
    loaderData: LicenseUsageDashboardPageLoaderData;
}
export interface LicenseUsageDashboardPageLoaderData {
    licenseUsage: LicenseUsageResource;
    licenseStatus: LicenseStatusResource;
}
const displayLimit = getKnownLicenseUsageNames();
export function getLimitsWithAlerts(limits: LicenseLimitUsage[]): LicenseLimitUsage[] {
    return limits.filter((l) => isKnownLicenseUsageName(l.Name) && limitStatusOrder.includes(l.LimitStatus)).sort((a, b) => limitStatusOrder.indexOf(a.LimitStatus) - limitStatusOrder.indexOf(b.LimitStatus));
}
export async function licenseUsagePageLoader(repository: Repository): Promise<LicenseUsageDashboardPageLoaderData> {
    const licenseUsageAsync = repository.Licenses.getCurrentUsage();
    const licenseStatusAsync = repository.Licenses.getCurrentStatus();
    return {
        licenseUsage: await licenseUsageAsync,
        licenseStatus: await licenseStatusAsync,
    };
}
const limitStatusOrder = [LimitStatus.ExceedingLimit, LimitStatus.InOverrun, LimitStatus.AtLimit, LimitStatus.AlmostAtLimit];
export const licenseUsagePageTitle = "License Usage Dashboard";
export function LicenseUsageDashboardPage({ loaderData }: LicenseUsageDashboardPageProps) {
    const isTrialExpirationVisibilityEnabled = useOctopusFeatureToggle("trial-expiration-visibility", false);
    const licenseUsage = loaderData.licenseUsage;
    const licenseStatus = loaderData.licenseStatus;
    const limitsWithAlerts = getLimitsWithAlerts(licenseUsage.Limits);
    const filteredAndOrderedUsageOverviews = displayLimit.map((limitName) => licenseUsage.Limits.find((l) => l.Name.toLowerCase() === limitName)).filter((item) => item !== undefined);
    const licenseDurationDays = moment(licenseStatus.EffectiveExpiryDate).diff(moment(licenseStatus.EffectiveStartDate), "days") + 1; // +1 to include the day of issue
    return (<PageContent header={{ title: licenseUsagePageTitle }}>
            <Section>
                <div className={styles.licenseOverviewContainer}>
                    <div className={styles.subHeader}>Overall Usage</div>
                    <div className={styles.cardList}>
                        {isTrialExpirationVisibilityEnabled && licenseStatus.IsTrial && (<LicenseUsageCard disposition={licenseStatus.Messages[0].Disposition} licenseDurationDays={licenseDurationDays} daysToEffectiveExpiryDate={licenseStatus.DaysToEffectiveExpiryDate}/>)}
                        {filteredAndOrderedUsageOverviews?.map((limit, index) => (<LimitUsageCard name={limit.Name} currentUsage={limit.CurrentUsage} licensedLimit={limit.LicensedLimit} disposition={limit.Disposition} isUnlimited={limit.IsUnlimited} key={index} contextualHelp={getLicenseUsageContextualHelp(limit?.Name?.toLowerCase())}/>))}
                    </div>
                </div>
            </Section>
            {isTrialExpirationVisibilityEnabled && licenseStatus.IsTrial && (<Section>
                    <LicenseCallout serialNumber={licenseStatus.SerialNumber} effectiveExpiryDate={licenseStatus.EffectiveExpiryDate} daysToEffectiveExpiryDate={licenseStatus.DaysToEffectiveExpiryDate} licenseDisposition={licenseStatus.Messages[0]?.Disposition}/>
                </Section>)}
            {!!limitsWithAlerts?.length && (<Section>
                    {limitsWithAlerts.map((limit, index) => (<div className={styles.usageAlertingContainer} key={`usage-alert-${index}`}>
                            <LicenseUsageCallout limitUsage={limit}/>
                        </div>))}
                </Section>)}
            <Section>
                <div className={styles.usageDetailTableContainer}>
                    <div className={styles.subHeader}>Usage by Space</div>
                    <UsageDetailTable spacesUsage={licenseUsage.SpacesUsage ?? []} isPtm={licenseUsage.IsPtm}/>
                </div>
            </Section>
        </PageContent>);
}
const styles = {
    cardList: css({
        gap: space[12],
        display: "grid",
        gridTemplateColumns: "repeat( auto-fit, minmax(230px, 1fr) )",
    }),
    subHeader: css({
        fontSize: fontSize["base"],
        fontWeight: fontWeight["600"],
        marginBottom: space[16],
    }),
    licenseOverviewContainer: css({
        marginTop: space[8],
        marginBottom: space[16],
    }),
    usageAlertingContainer: css({
        marginTop: space[16],
        marginBottom: space[16],
    }),
    usageDetailTableContainer: css({
        marginTop: space[32],
        marginBottom: space[16],
    }),
};
